import { useRef, useState, type ChangeEvent } from 'react';
import { apiClient } from '../../services/apiClient';
import './ImageUploadField.css';

const ACCEPT = 'image/png,image/jpeg,image/webp,image/gif';

type Props = {
  /** 現在の画像URL */
  value: string;
  /** アップロード完了 or 手入力で URL が変わったときに呼ばれる */
  onChange: (url: string) => void;
  /** URL 入力欄の className (呼び出し側フォームのスタイルに合わせる) */
  inputClassName?: string;
  placeholder?: string;
};

/**
 * 画像URLの手入力に加えて、ファイルを選んで直接アップロードできる共通入力。
 * アップロードは汎用の画像アップロードエンドポイント (/products/upload-image,
 * Supabase Storage の media バケットへ保存し公開URLを返す) を再利用する。
 */
export default function ImageUploadField({
  value,
  onChange,
  inputClassName = 'admin-form-input',
  placeholder = 'https://... または画像をアップロード',
}: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // 同じファイルを続けて選び直せるようにする
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await apiClient.post<{ url: string }>('/products/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onChange(data.url);
    } catch {
      setError('アップロードに失敗しました。時間をおいて再度お試しください。');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="image-upload-field">
      <div className="image-upload-field-row">
        {value && <img className="image-upload-field-thumb" src={value} alt="" />}
        <input
          className={inputClassName}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
        />
        <button
          type="button"
          className="image-upload-field-btn"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? 'アップロード中…' : 'アップロード'}
        </button>
      </div>
      {error && <p className="image-upload-field-error">{error}</p>}
      <input
        ref={fileRef}
        type="file"
        accept={ACCEPT}
        onChange={handleFile}
        style={{ display: 'none' }}
      />
    </div>
  );
}
